import {
  ZeroEtlJobsApi,
  ConditionOperatorEnum,
  JobStatus,
  SortOrder,
  StringOperators,
  ZeroEtlJobsSortField,
  ZeroEtlJobSyncStatus,
  ZeroEtlJobType,
  ZeroEtlTargetTablesSortField,
  ZeroEtlTargetTableStatus,
} from '@repo/api-gw-sdk';
import type {
  Condition,
  Configuration,
  ZeroEtlJobsFilters,
  ZeroEtlJobSyncsFilters,
  ZeroEtlTargetTablesFilters,
  ZeroEtlJobSync,
} from '@repo/api-gw-sdk';
import type { ColumnSort } from '@tanstack/react-table';

import {
  conditionToAccountIdExpression,
  conditionToCloudProviderExpression,
  conditionToResourceIdExpression,
  conditionToResourceNameExpression,
  conditionToResourceTypeExpression,
  conditionToSourceRegionExpression,
  toScalarOperator,
  toStringOperator,
} from '@/components/resourcesSelection/expressionHelpers';
import { collectValueConditions } from '@/types/advanceFilter';
import { encodePageToken } from '@/utils/string';

import type { HttpClient } from './httpClient';

const baseUrl = '/jobs';

const JOB_DETAIL_REFRESH_INTERVAL_MS = 15_000;
const JOB_LIST_REFRESH_INTERVAL_MS = 30_000;

const ACTIVE_JOB_STATUSES = new Set([
  JobStatus.JOB_RUNNING,
  JobStatus.JOB_PENDING,
  JobStatus.JOB_UNSPECIFIED,
]);

const isActiveJobStatus = (status: JobStatus | undefined) =>
  !status || ACTIVE_JOB_STATUSES.has(status);

// Sync states that are still moving, so the detail panel keeps polling.
const activeSyncStatuses = new Set<ZeroEtlJobSyncStatus>([
  ZeroEtlJobSyncStatus.ZERO_ETL_SYNC_PENDING,
  ZeroEtlJobSyncStatus.ZERO_ETL_SYNC_RUNNING,
]);

const jobStatusValues = new Set<string>(Object.values(JobStatus));
const isJobStatus = (value: string): value is JobStatus =>
  jobStatusValues.has(value);

const zeroEtlJobTypeValues = new Set<string>(Object.values(ZeroEtlJobType));
const isZeroEtlJobType = (value: string): value is ZeroEtlJobType =>
  zeroEtlJobTypeValues.has(value);

type ZeroEtlResourceFilters = Pick<
  ZeroEtlJobsFilters,
  | 'resourceIds'
  | 'resourceNames'
  | 'resourceName'
  | 'resourceTypes'
  | 'cloudProviders'
  | 'sourceAccountIds'
  | 'sourceRegions'
>;

const applyResourceCondition = (
  filters: ZeroEtlResourceFilters,
  condition: Condition
) => {
  const values = condition.value ?? [];
  const scalarOperator = toScalarOperator(condition.operator);
  const stringOperator = toStringOperator(condition.operator);

  if (condition.property === 'resourceId' && scalarOperator) {
    filters.resourceIds = conditionToResourceIdExpression(
      values,
      scalarOperator
    ).resourceId.resourceIds;
    return true;
  }
  if (condition.property === 'resourceName' && stringOperator) {
    const expression = conditionToResourceNameExpression(
      values,
      stringOperator
    ).resourceName;
    if (expression.operator === StringOperators.STRING_CONTAINS_OPERATOR) {
      filters.resourceName = expression.resourceNames[0];
    } else {
      filters.resourceNames = expression.resourceNames;
    }
    return true;
  }
  if (condition.property === 'resourceType' && scalarOperator) {
    filters.resourceTypes = conditionToResourceTypeExpression(
      values,
      scalarOperator
    ).resourceType.resourceTypes;
    return true;
  }
  if (condition.property === 'sourceCloudProvider' && scalarOperator) {
    filters.cloudProviders = conditionToCloudProviderExpression(
      values,
      scalarOperator
    ).cloudProvider.cloudProviders;
    return true;
  }
  if (condition.property === 'accountId' && scalarOperator) {
    filters.sourceAccountIds = conditionToAccountIdExpression(
      values,
      scalarOperator
    ).accountId.accountIds;
    return true;
  }
  if (condition.property === 'sourceRegion' && scalarOperator) {
    filters.sourceRegions = conditionToSourceRegionExpression(
      values,
      scalarOperator
    ).sourceRegion.regions;
    return true;
  }
  return false;
};

const zeroEtlJobsSortFields: Record<string, ZeroEtlJobsSortField> = {
  createdTime: ZeroEtlJobsSortField.ZERO_ETL_JOBS_SORT_FIELD_CREATED_TIME,
  startTime: ZeroEtlJobsSortField.ZERO_ETL_JOBS_SORT_FIELD_START_TIME,
  endTime: ZeroEtlJobsSortField.ZERO_ETL_JOBS_SORT_FIELD_END_TIME,
  status: ZeroEtlJobsSortField.ZERO_ETL_JOBS_SORT_FIELD_STATUS,
};

// The zero-etl list API takes typed filters rather than the query-builder
// Condition JSON the other job lists accept, so the table's Condition tree is
// flattened here into the fields the API understands.
const toZeroEtlJobFilters = (
  condition: Condition | undefined
): ZeroEtlJobsFilters | undefined => {
  if (!condition) {
    return undefined;
  }
  const filters: ZeroEtlJobsFilters = {};
  for (const valueCondition of collectValueConditions(condition)) {
    if (applyResourceCondition(filters, valueCondition)) {
      continue;
    }
    const values = valueCondition.value ?? [];
    switch (valueCondition.property) {
      case 'status':
        filters.statuses = values.filter(isJobStatus);
        break;
      case 'id':
        filters.jobId = values[0];
        break;
      case 'jobType':
        filters.jobTypes = values.filter(isZeroEtlJobType);
        break;
      case 'accountDisplayName':
        filters.sourceAccountName = values[0];
        break;
      case 'policyId':
        filters.policyIds = values;
        break;
      case 'backupJobId':
        filters.backupJobId = values[0];
        break;
      case 'snapshotId':
        filters.snapshotId = values[0];
        break;
      case 'region':
        filters.regions = values;
        break;
      case 'vaultId':
        filters.vaultIds = values;
        break;
      case 'targetNamespace':
        filters.targetNamespace = values[0];
        break;
      case 'targetTable':
        filters.targetTable = values[0];
        break;
      case 'createdTime':
        if (valueCondition.operator === ConditionOperatorEnum.From) {
          filters.createdAfter = new Date(values[0]);
        } else if (valueCondition.operator === ConditionOperatorEnum.To) {
          filters.createdBefore = new Date(values[0]);
        }
        break;
      case 'startTime':
        if (valueCondition.operator === ConditionOperatorEnum.From) {
          filters.startedAfter = new Date(values[0]);
        } else if (valueCondition.operator === ConditionOperatorEnum.To) {
          filters.startedBefore = new Date(values[0]);
        }
        break;
      case 'endTime':
        if (valueCondition.operator === ConditionOperatorEnum.From) {
          filters.endedAfter = new Date(values[0]);
        } else if (valueCondition.operator === ConditionOperatorEnum.To) {
          filters.endedBefore = new Date(values[0]);
        }
        break;
    }
  }
  return filters;
};

const targetStatusValues = new Set<string>(
  Object.values(ZeroEtlTargetTableStatus)
);
const isTargetStatus = (value: string): value is ZeroEtlTargetTableStatus =>
  targetStatusValues.has(value);

const zeroEtlTargetTablesSortFields: Record<
  string,
  ZeroEtlTargetTablesSortField
> = {
  updatedTime:
    ZeroEtlTargetTablesSortField.ZERO_ETL_TARGET_TABLES_SORT_FIELD_UPDATED_TIME,
  icebergTable:
    ZeroEtlTargetTablesSortField.ZERO_ETL_TARGET_TABLES_SORT_FIELD_ICEBERG_TABLE,
  status: ZeroEtlTargetTablesSortField.ZERO_ETL_TARGET_TABLES_SORT_FIELD_STATUS,
  completeThroughPit:
    ZeroEtlTargetTablesSortField.ZERO_ETL_TARGET_TABLES_SORT_FIELD_COMPLETE_THROUGH_PIT,
};

const toZeroEtlTargetTableFilters = (
  condition: Condition | undefined
): ZeroEtlTargetTablesFilters | undefined => {
  if (!condition) {
    return undefined;
  }
  const filters: ZeroEtlTargetTablesFilters = {};
  for (const valueCondition of collectValueConditions(condition)) {
    if (applyResourceCondition(filters, valueCondition)) {
      continue;
    }
    const values = valueCondition.value ?? [];
    switch (valueCondition.property) {
      // The single UI filter matches the fully-qualified "namespace.table"
      // name the column shows; the bare namespace/table filters stay API-only.
      case 'icebergTable':
        filters.icebergFullName = values[0];
        break;
      case 'status':
        filters.statuses = values.filter(isTargetStatus);
        break;
      case 'policyId':
        filters.policyIds = values;
        break;
      case 'vaultId':
        filters.vaultIds = values;
        break;
      case 'accountDisplayName':
        filters.sourceAccountName = values[0];
        break;
      case 'completeThroughPit':
        if (valueCondition.operator === ConditionOperatorEnum.From) {
          filters.lastFullSyncAfter = new Date(values[0]);
        } else if (valueCondition.operator === ConditionOperatorEnum.To) {
          filters.lastFullSyncBefore = new Date(values[0]);
        }
        break;
      case 'updatedTime':
        if (valueCondition.operator === ConditionOperatorEnum.From) {
          filters.updatedAfter = new Date(values[0]);
        } else if (valueCondition.operator === ConditionOperatorEnum.To) {
          filters.updatedBefore = new Date(values[0]);
        }
        break;
    }
  }
  return filters;
};

export const zeroEtlJobs = (
  httpClient: HttpClient,
  config: Configuration,
  currentProjectId: string
) => {
  const zeroEtlJobsApi = new ZeroEtlJobsApi(config);
  return {
    useList: (
      pageIndex: number,
      pageSize: number,
      sorting: ColumnSort | undefined,
      columnFilters: Condition | undefined
    ) =>
      httpClient.executeWithConfig(
        // The page token embeds a fresh uuid on every call, so it can only be built inside the fetcher: in the
        // argument list it would land in the SWR key and refetch on every render. The key carries the page index.
        (...args: Parameters<typeof zeroEtlJobsApi.listZeroEtlJobs>) =>
          zeroEtlJobsApi.listZeroEtlJobs(
            args[0],
            pageIndex ? encodePageToken(pageIndex) : undefined,
            args[2],
            args[3]
          ),
        baseUrl,
        '/zero-etl',
        { refreshInterval: JOB_LIST_REFRESH_INTERVAL_MS },
        currentProjectId,
        `${pageIndex}`,
        pageSize,
        {
          filters: toZeroEtlJobFilters(columnFilters),
          sorts:
            sorting && zeroEtlJobsSortFields[sorting.id]
              ? [
                  {
                    field: zeroEtlJobsSortFields[sorting.id],
                    order: sorting.desc ? SortOrder.Desc : SortOrder.Asc,
                  },
                ]
              : undefined,
        }
      ),
    // Single job by id — what the detail panel opens from, so a deep link
    // (?id=) hydrates without the list page it came from. Polls while the job
    // is still active, like the other job detail panels.
    useJob: (jobId: string) =>
      httpClient.executeWithConfig(
        zeroEtlJobsApi.listZeroEtlJobs.bind(zeroEtlJobsApi),
        jobId ? baseUrl : null,
        '/zero-etl/job',
        {
          refreshInterval: (data) =>
            isActiveJobStatus(data?.data?.[0]?.status)
              ? JOB_DETAIL_REFRESH_INTERVAL_MS
              : 0,
        },
        currentProjectId,
        undefined,
        1,
        { filters: { jobId } }
      ),
    useJobSyncs: (
      jobId: string,
      pageToken: string | undefined,
      pageSize: number,
      filters: ZeroEtlJobSyncsFilters | undefined
    ) =>
      httpClient.executeWithConfig(
        zeroEtlJobsApi.listZeroEtlJobSyncs.bind(zeroEtlJobsApi),
        jobId ? baseUrl : null,
        '/zero-etl/syncs',
        {
          refreshInterval: (data) =>
            data?.data?.some((sync: ZeroEtlJobSync) =>
              activeSyncStatuses.has(sync.status)
            )
              ? JOB_DETAIL_REFRESH_INTERVAL_MS
              : 0,
        },
        currentProjectId,
        jobId,
        pageToken,
        pageSize,
        { filters }
      ),
    useTargetTables: (
      pageIndex: number,
      pageSize: number,
      sorting: ColumnSort | undefined,
      columnFilters: Condition | undefined
    ) =>
      httpClient.executeWithConfig(
        // Token built inside the fetcher so its per-call uuid stays out of the SWR key, which carries the page index.
        (...args: Parameters<typeof zeroEtlJobsApi.listZeroEtlTargetTables>) =>
          zeroEtlJobsApi.listZeroEtlTargetTables(
            args[0],
            pageIndex ? encodePageToken(pageIndex) : undefined,
            args[2],
            args[3]
          ),
        baseUrl,
        '/zero-etl/target-tables',
        { refreshInterval: JOB_LIST_REFRESH_INTERVAL_MS },
        currentProjectId,
        `${pageIndex}`,
        pageSize,
        {
          filters: toZeroEtlTargetTableFilters(columnFilters),
          sorts:
            sorting && zeroEtlTargetTablesSortFields[sorting.id]
              ? [
                  {
                    field: zeroEtlTargetTablesSortFields[sorting.id],
                    order: sorting.desc ? SortOrder.Desc : SortOrder.Asc,
                  },
                ]
              : undefined,
        }
      ),
    // Single iceberg table by id — same role as useJob for the target-table
    // panel; polls while its last job is still syncing.
    useTargetTable: (id: string) =>
      httpClient.executeWithConfig(
        zeroEtlJobsApi.listZeroEtlTargetTables.bind(zeroEtlJobsApi),
        id ? baseUrl : null,
        '/zero-etl/target-table',
        {
          refreshInterval: (data) =>
            data?.data?.[0]?.status ===
            ZeroEtlTargetTableStatus.ZERO_ETL_TARGET_TABLE_RUNNING
              ? JOB_DETAIL_REFRESH_INTERVAL_MS
              : 0,
        },
        currentProjectId,
        undefined,
        1,
        { filters: { ids: [id] } }
      ),
  };
};
